import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bot, Sparkles, BarChart3, Briefcase, TrendingUp } from "lucide-react";
import { Tables } from "@/types/database.types";
import { AgentCardSkeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { SmartIcon } from "@/components/ui/SmartIcon";

type AIAssistant = Tables<"ai_assistants">;

interface PublicAIAssistantsSectionProps {
  assistants: AIAssistant[];
  isLoading: boolean;
  searchQuery?: string;
  selectedCategory?: string;
  hideIndustryTags?: boolean;
}

const getCategoryColor = (category: string) => {
  switch (category) {
    case "Marketing":
      return "bg-blue-100 text-blue-800";
    case "Sales":
      return "bg-green-100 text-green-800";
    case "Daily work":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case "Marketing":
      return <BarChart3 className="h-4 w-4" />;
    case "Sales":
      return <TrendingUp className="h-4 w-4" />;
    case "Daily work":
      return <Briefcase className="h-4 w-4" />;
    default:
      return <Bot className="h-4 w-4" />;
  }
};

const getIndustryColor = (industryName: string) => {
  // Generate a consistent color based on industry name
  const colors = [
    "bg-blue-100 text-blue-800 border-blue-200",
    "bg-green-100 text-green-800 border-green-200",
    "bg-purple-100 text-purple-800 border-purple-200",
    "bg-amber-100 text-amber-800 border-amber-200",
    "bg-rose-100 text-rose-800 border-rose-200",
    "bg-cyan-100 text-cyan-800 border-cyan-200",
    "bg-indigo-100 text-indigo-800 border-indigo-200",
    "bg-emerald-100 text-emerald-800 border-emerald-200",
    "bg-orange-100 text-orange-800 border-orange-200",
    "bg-pink-100 text-pink-800 border-pink-200",
  ];

  const hash = industryName
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export const PublicAIAssistantsSection: React.FC<
  PublicAIAssistantsSectionProps
> = ({
  assistants,
  isLoading,
  searchQuery = "",
  selectedCategory = "All",
  hideIndustryTags = false,
}) => {
  const navigate = useNavigate();

  const handleUseAssistant = () => {
    navigate("/register");
  };

  return (
    <div className="space-y-4">
      {/* AI Assistants Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <AgentCardSkeleton key={i} />
          ))}
        </div>
      ) : assistants.length === 0 ? (
        <Card className="bg-muted/50">
          <CardContent className="py-8 flex flex-col items-center justify-center">
            <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {searchQuery.trim()
                ? "No assistants found"
                : selectedCategory === "All"
                  ? "No AI assistants available"
                  : `No ${selectedCategory} assistants found`}
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              {searchQuery.trim()
                ? `No assistants match "${searchQuery}". Try adjusting your search terms or selecting a different category.`
                : selectedCategory === "All"
                  ? "AI assistants are powerful tools for generating content like emails, posts, and documents. Sign up to start using them."
                  : `No assistants found in the ${selectedCategory} category. Try selecting a different category or "All" to see all available assistants.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative">
          {assistants.map((assistant) => {
            return (
              <Card
                key={assistant.id}
                className="group hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer border border-gray-200 hover:border-gray-300 transform relative overflow-visible"
                onClick={handleUseAssistant}
                title="Sign up to use this assistant"
              >
                {/* Banner - full width at top */}
                <div
                  className={`h-16 w-full relative overflow-hidden rounded-t-lg z-0 ${
                    getCategoryColor(assistant.category).includes("blue")
                      ? "bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-700"
                      : getCategoryColor(
                            assistant.category || "Daily work",
                          ).includes("green")
                        ? "bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700"
                        : getCategoryColor(
                              assistant.category || "Daily work",
                            ).includes("amber")
                          ? "bg-gradient-to-br from-amber-500 via-amber-600 to-yellow-700"
                          : getCategoryColor(
                                assistant.category || "Daily work",
                              ).includes("purple")
                            ? "bg-gradient-to-br from-purple-500 via-violet-600 to-indigo-700"
                            : "bg-gradient-to-br from-slate-500 via-gray-600 to-zinc-700"
                  }`}
                >
                  {/* Background image with overlay */}
                  {assistant.banner && (
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60 z-0 pointer-events-none"
                      style={{ backgroundImage: `url(${assistant.banner})` }}
                    />
                  )}

                  {/* Futuristic overlay pattern */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 z-0 pointer-events-none"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-0 pointer-events-none"></div>
                </div>

                <div className="absolute left-4 z-20" style={{ top: "1.4rem" }}>
                  <div
                    className="bg-white rounded-full transform group-hover:scale-110 transition-transform duration-300 shadow-sm ring-1 ring-black/5"
                    style={{ padding: "0.15rem" }}
                  >
                    <SmartIcon
                      iconUrl={assistant.icon_url}
                      size={50}
                      fallbackIcon={getCategoryIcon(
                        assistant.category || "Daily work",
                      )}
                      alt={`${assistant.title || "Assistant"} icon`}
                    />
                  </div>
                </div>

                <CardContent className="p-4 relative z-10">
                  {/* Content with custom padding for title */}
                  <div style={{ paddingBottom: "6px" }}>
                    {/* Title - Always visible */}
                    <h3 className="font-semibold text-sm leading-tight text-left mb-2">
                      {assistant.title}
                    </h3>

                    {/* Industry Tag - only show when not explicitly hidden */}
                    {!hideIndustryTags && (assistant as any).industries && (
                      <div className="flex items-center gap-1">
                        <Badge
                          variant="outline"
                          className={`text-xs px-2 py-0.5 font-medium border ${getIndustryColor((assistant as any).industries.name)}`}
                        >
                          {(assistant as any).industries.emoji && (
                            <span className="mr-1">
                              {(assistant as any).industries.emoji}
                            </span>
                          )}
                          {(assistant as any).industries.name}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
